import { Store } from '@/core/flux.service';
import { hierarchyOutputToHierarchyInput } from '@/tableDefinitionEditor/assetBuilder.utilities';
import type { PersistenceLevel } from '@/core/flux.service';
import type { HierarchyInputV1 } from '@seeqdev/api/model/HierarchyInputV1';
import type { HierarchyOutputV1 } from '@seeqdev/api/model/HierarchyOutputV1';
import type { ViewColumnInputV1 } from '@seeqdev/api/model/ViewColumnInputV1';

interface AssetBuilderState {
  id: string | undefined;
  name: string;
  description: string | undefined;
  aliasColumnId: string;
  hierarchyOutput: HierarchyOutputV1 | undefined;
  selectedTreeOption: undefined;
  // Properties from HierarchyInputV1 that might be set by setHierarchyOutput
  filterGrouping?: string;
  tableDefinitionId?: string;
  viewColumns?: ViewColumnInputV1[];
  // Column IDs of the current row group columns in ag-grid
  rowGroupColumnIds: string[];
}

export class AssetBuilderStore extends Store {
  persistenceLevel: PersistenceLevel = 'NONE';
  static readonly storeName = 'sqAssetBuilderStore';

  initialize(): void {
    const initialState: AssetBuilderState = {
      id: undefined,
      name: '',
      description: undefined,
      aliasColumnId: '',
      hierarchyOutput: undefined,
      selectedTreeOption: undefined,
      rowGroupColumnIds: [],
    };
    this.state = this.immutable(initialState);
  }

  get id(): string | undefined {
    return this.state.get('id');
  }
  get name(): string {
    return this.state.get('name');
  }
  get description(): string | undefined {
    return this.state.get('description');
  }
  get aliasColumnId(): string {
    return this.state.get('aliasColumnId');
  }

  get hierarchyOutput(): HierarchyOutputV1 | undefined {
    return this.state.get('hierarchyOutput');
  }

  get rowGroupColumnIds(): string[] {
    return this.state.get('rowGroupColumnIds');
  }

  setName = ({ name }: { name: string }): void => {
    this.state.set('name', name);
  };
  setDescription = ({ description }: { description: string | undefined }): void => {
    this.state.set('description', description);
  };
  setAliasColumnId = ({ aliasColumnId }: { aliasColumnId: string }): void => {
    this.state.set('aliasColumnId', aliasColumnId);
  };
  setHierarchyOutput = ({ hierarchyOutput }: { hierarchyOutput: HierarchyOutputV1 }): void => {
    const hierarchyInput = hierarchyOutputToHierarchyInput(hierarchyOutput);
    const keys = Object.keys(hierarchyInput) as (keyof HierarchyInputV1)[];
    keys.forEach((key) => {
      this.state.set(key, hierarchyInput[key]);
    });
    this.state.set('id', hierarchyOutput.id);
    this.state.set('hierarchyOutput', hierarchyOutput);
  };

  setRowGroupColumnIds = ({ rowGroupColumnIds }: { rowGroupColumnIds: string[] }): void => {
    this.state.set('rowGroupColumnIds', rowGroupColumnIds);
  };

  addRowGroupColumn = ({ columnId }: { columnId: string }): void => {
    const currentIds = this.rowGroupColumnIds;
    if (!currentIds.includes(columnId)) {
      this.state.set('rowGroupColumnIds', [...currentIds, columnId]);
    }
  };

  removeRowGroupColumn = ({ columnId }: { columnId: string }): void => {
    const currentIds = this.rowGroupColumnIds;
    this.state.set(
      'rowGroupColumnIds',
      currentIds.filter((id) => id !== columnId),
    );
  };

  reorderRowGroupColumns = ({ fromIndex, toIndex }: { fromIndex: number; toIndex: number }): void => {
    const currentIds = [...this.rowGroupColumnIds];
    const length = currentIds.length;

    if (fromIndex < 0 || fromIndex >= length || toIndex < 0 || toIndex >= length) {
      return;
    }

    const [removed] = currentIds.splice(fromIndex, 1);
    currentIds.splice(toIndex, 0, removed);
    this.state.set('rowGroupColumnIds', currentIds);
  };

  reset = (): void => {
    this.initialize();
  };

  handlers = {
    ASSET_BUILDER_SET_NAME: this.setName,
    ASSET_BUILDER_SET_DESCRIPTION: this.setDescription,
    ASSET_BUILDER_SET_ALIAS_COLUMN_ID: this.setAliasColumnId,
    ASSET_BUILDER_SET_HIERARCHY_OUTPUT: this.setHierarchyOutput,
    ASSET_BUILDER_SET_ROW_GROUP_COLUMN_IDS: this.setRowGroupColumnIds,
    ASSET_BUILDER_ADD_ROW_GROUP_COLUMN: this.addRowGroupColumn,
    ASSET_BUILDER_REMOVE_ROW_GROUP_COLUMN: this.removeRowGroupColumn,
    ASSET_BUILDER_REORDER_ROW_GROUP_COLUMNS: this.reorderRowGroupColumns,
    ASSET_BUILDER_RESET: this.reset,
  };
}
